import type { RestaurantWithSlots } from "@/lib/store";

export interface MealDiff {
  /** Slot strings present in the new snapshot but absent from the previous one. */
  newSlots: Set<string>;
  /** True when some previously-available slots are gone but at least one slot still remains. */
  someTaken: boolean;
}

export interface RestaurantDiff {
  breakfast: MealDiff;
  lunch: MealDiff;
  dinner: MealDiff;
}

export type AvailabilityDiff = Map<string, RestaurantDiff>;

function diffMeal(oldSlots: string[], newSlots: string[]): MealDiff {
  const oldSet = new Set(oldSlots);
  const newSet = new Set(newSlots);
  const newlyAvailable = new Set(newSlots.filter((s) => !oldSet.has(s)));
  const someTaken = newSlots.length > 0 && oldSlots.some((s) => !newSet.has(s));
  return { newSlots: newlyAvailable, someTaken };
}

/**
 * Compares two consecutive availability snapshots, keyed by restaurant name, and
 * returns a per-restaurant/per-meal diff. `prev === null` means "no baseline yet"
 * (e.g. first load) and yields an empty diff — no highlights.
 *
 * A restaurant absent from `prev` naturally gets `oldSlots = []` for both meals,
 * so all of its slots land in `newSlots` and `someTaken` stays `false` — no
 * special-casing needed for brand-new restaurants.
 */
export function computeAvailabilityDiff(
  prev: RestaurantWithSlots[] | null,
  next: RestaurantWithSlots[]
): AvailabilityDiff {
  const diff: AvailabilityDiff = new Map();
  if (!prev) return diff;

  const prevByName = new Map(prev.map((r) => [r.name, r]));
  for (const r of next) {
    const p = prevByName.get(r.name);
    diff.set(r.name, {
      breakfast: diffMeal(p?.breakfastSlots ?? [], r.breakfastSlots),
      lunch: diffMeal(p?.lunchSlots ?? [], r.lunchSlots),
      dinner: diffMeal(p?.dinnerSlots ?? [], r.dinnerSlots),
    });
  }
  return diff;
}
